import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { LANGUAGE_LABELS, SUPPORTED_LANGUAGES, type SupportedLanguage } from '../../i18n'
import {
  getLandingPageBySlug,
  getLandingPageSibling,
  getProgrammaticLandingPageBySlug,
  getProgrammaticLandingPagesByTopic,
} from '../../content/landingPages'
import { getBlogPostBySlug, getBlogPostSibling, blogListPath, blogPostPath, parseBlogPath } from '../../content/blog'

export function LanguageSwitcher() {
  const { i18n, t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const current = (SUPPORTED_LANGUAGES as readonly string[]).includes(i18n.resolvedLanguage ?? '')
    ? (i18n.resolvedLanguage as SupportedLanguage)
    : 'de'

  function handleChange(targetLang: SupportedLanguage) {
    // On an SEO landing page, jump to its translated sibling (same topic, different language)
    // instead of just the target language's homepage - see content/landingPages/index.ts.
    const currentPage = getLandingPageBySlug(location.pathname.replace(/^\//, ''))
    if (currentPage) {
      const sibling = getLandingPageSibling(currentPage, targetLang)
      navigate(sibling ? `/${sibling.slug}` : `/${targetLang}`)
      return
    }

    // Same idea for a programmatic pSEO page (pSEO-Story Task 04) - separate registry from
    // LANDING_PAGES above (see content/landingPages/index.ts), so it needs its own lookup rather
    // than falling through to the plain-homepage default below and losing the "I'm looking at
    // this size" context.
    const currentProgrammaticPage = getProgrammaticLandingPageBySlug(location.pathname.replace(/^\//, ''))
    if (currentProgrammaticPage) {
      const sibling = getProgrammaticLandingPagesByTopic(currentProgrammaticPage.topicId).find(
        (page) => page.lang === targetLang,
      )
      navigate(sibling ? `/${sibling.slug}` : `/${targetLang}`)
      return
    }

    // Same idea for the blog (see content/blog/index.ts): jump to the translated sibling article
    // if one exists, otherwise the target language's blog index rather than losing the visitor's
    // "I'm reading the blog" context by bouncing them to the homepage.
    const blogRoute = parseBlogPath(location.pathname)
    if (blogRoute) {
      const currentPost = blogRoute.slug ? getBlogPostBySlug(blogRoute.slug, blogRoute.lang) : undefined
      const sibling = currentPost && getBlogPostSibling(currentPost, targetLang)
      navigate(sibling ? blogPostPath(sibling) : blogListPath(targetLang))
      return
    }

    navigate(`/${targetLang}`)
  }

  return (
    <select
      className="language-switcher"
      aria-label={t('common.languageSwitcher')}
      value={current}
      onChange={(e) => handleChange(e.target.value as SupportedLanguage)}
    >
      {SUPPORTED_LANGUAGES.map((lng) => (
        <option key={lng} value={lng}>
          {LANGUAGE_LABELS[lng]}
        </option>
      ))}
    </select>
  )
}
